/**
 * API calls for the Phase 2 features (docs/phase2).
 *
 * Kept in one module rather than spread across `api.ts` so the Phase 2 surface
 * is easy to find and, if a gate never clears, easy to leave unused.
 */

import { request } from "./api";

// ---------------------------------------------------------------------------
// inbound records from a lab system (IN-xx)
// ---------------------------------------------------------------------------
export interface InboundRecord {
  id: string;
  source: string;
  external_id: string;
  status: "pending" | "used" | "discarded";
  isolate_id: string | null;
  discarded_reason: string | null;
  created_at: string;
}

export interface InboundPrefill {
  id: string;
  source: string;
  external_id: string;
  status: InboundRecord["status"];
  /** The entry form's shape, as far as the lab's wording could be resolved. */
  prefill: Record<string, unknown>;
  /** Fields the lab's wording did not match. The volunteer must choose these. */
  unresolved: string[];
  /** Columns the adapter could not map at all, kept so nothing is lost. */
  unmapped: Record<string, unknown>;
}

export const inbound = {
  pending: () => request<InboundRecord[]>("/inbound/pending"),
  get: (id: string) => request<InboundPrefill>(`/inbound/${id}`),
  summary: () => request<Record<string, number>>("/inbound/stats/summary"),
  link: (id: string, isolateId: string) =>
    request<InboundRecord>(`/inbound/${id}/link`, {
      method: "POST",
      body: { isolate_id: isolateId },
    }),
  discard: (id: string, reason: string) =>
    request<InboundRecord>(`/inbound/${id}/discard`, {
      method: "POST",
      body: { reason },
    }),
};

// ---------------------------------------------------------------------------
// publications (PB-xx)
// ---------------------------------------------------------------------------
export interface Publication {
  id: string;
  analysis_run_id: string;
  title: string;
  period_label: string;
  as_of_month: string;
  notes: string;
  status: "draft" | "approved" | "published" | "withdrawn";
  proposed_by: string;
  approved_by: string | null;
  approved_at: string | null;
  published_at: string | null;
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
  is_current: boolean;
  suppression: Record<string, unknown>;
  created_at: string;
}

export interface PublicationDetail extends Publication {
  /** The redacted payload, so a reviewer sees exactly what would go out. */
  payload: Record<string, unknown>;
}

export const publications = {
  list: () => request<Publication[]>("/publications"),
  get: (id: string) => request<PublicationDetail>(`/publications/${id}`),
  current: () => request<PublicationDetail>("/publications/current"),
  propose: (body: {
    analysis_run_id: string;
    title: string;
    period_label?: string;
    notes?: string;
    percent_threshold?: number;
    count_threshold?: number;
  }) => request<PublicationDetail>("/publications", { method: "POST", body }),
  approve: (id: string) =>
    request<Publication>(`/publications/${id}/approve`, { method: "POST" }),
  publish: (id: string) =>
    request<Publication>(`/publications/${id}/publish`, { method: "POST" }),
  withdraw: (id: string, reason: string) =>
    request<Publication>(`/publications/${id}/withdraw`, {
      method: "POST",
      body: { reason },
    }),
};

// ---------------------------------------------------------------------------
// breakpoints (BP-xx)
// ---------------------------------------------------------------------------
export interface BreakpointSet {
  id: string;
  standard: string;
  version: string;
  year: number | null;
  source_url: string | null;
  notes: string;
  imported_at: string;
  /** The set new entry is checked against. Exactly one at a time. */
  is_active: boolean;
  breakpoint_count: number;
}

export interface Disagreement {
  isolate_id: string;
  record_id: string;
  organism_code: string | null;
  disagreements: Array<{
    antibiotic: string;
    entered: string;
    computed: string;
    measurement: number;
    method: string;
  }>;
}

export const breakpoints = {
  sets: () => request<BreakpointSet[]>("/breakpoints/sets"),
  activate: (id: string) =>
    request<BreakpointSet>(`/breakpoints/sets/${id}/activate`, { method: "POST" }),
  reinterpret: (id: string) =>
    request<Record<string, number>>(`/breakpoints/sets/${id}/reinterpret`, {
      method: "POST",
    }),
  disagreements: (id: string) =>
    request<Disagreement[]>(`/breakpoints/sets/${id}/disagreements`),
};
